import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { alertMonitoringApi } from '../api/adminAlert.api';
import PageHeader from '../components/PageHeader';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
const PIE_COLORS = ['#10B981', '#EF4444'];

const AlertAnalytics = () => {
  const [data, setData] = useState({ types: [], delivery: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await alertMonitoringApi.getAnalytics();
      if (res.success) {
        setData(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-6">Loading analytics data...</div>;

  return (
    <>
      <PageHeader
        title="Alert Analytics"
        icon="fa-chart-line"
        trail={[{ label: 'Alerts' }, { label: 'Analytics' }]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* ALERT TYPES DISTRIBUTION */}
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Alerts by Type (Last 30 Days)</h2>
          <div className="h-80">
            {data.types && data.types.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.types} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{fontSize: 12}} />
                  <YAxis />
                  <RechartsTooltip cursor={{fill: 'transparent'}} />
                  <Legend />
                  <Bar dataKey="value" name="Total Alerts" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 italic">No alert data available yet.</div>
            )}
          </div>
        </div>

        {/* DELIVERY SUCCESS RATE */}
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Notification Delivery Success</h2>
          <div className="h-80 flex items-center justify-center relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.delivery}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={120}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {data.delivery.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center mt-[-30px]">
                <p className="text-3xl font-bold text-gray-800">{data.delivery.length > 0 ? data.delivery[0].value : 0}%</p>
                <p className="text-xs text-gray-500 uppercase font-semibold">Success</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ADDITIONAL METRICS - PLACEHOLDERS FOR NOW */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Response Time Distribution</h2>
          <div className="h-48 flex items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <p className="text-gray-400 text-sm italic">Histogram will populate as more data is collected</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">False Alarm Heatmap</h2>
          <div className="h-48 flex items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <p className="text-gray-400 text-sm italic">Geographic heatmap will populate here</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AlertAnalytics;
